import React, { useState } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { EventListing, getRSVPCount, getUserRSVP, publishRSVP, deleteEventListing } from '../lib/eventListings';
import { recordSignal } from '../lib/signals';
import { asText } from '../lib/sanitize';
import { ExternalLink } from './ExternalLink';
import { ReportButton } from './ReportButton';
import { loadOrCreateIdentity } from '../lib/identity';
import { publishEvent } from '../lib/sync';

interface Props {
  listing: EventListing;
  onBack: () => void;
  onReload?: () => void;
}

function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export function EventDetail({ listing, onBack, onReload }: Props) {
  const identity = loadOrCreateIdentity();
  const counts = getRSVPCount(listing.id);
  const myRSVP = getUserRSVP(listing.id, identity.userId);
  const tags = listing.tags ?? [];
  const [shared, setShared] = useState(false);

  const handleRSVP = async (status: 'going' | 'interested') => {
    await publishRSVP(listing.id, status);
    recordSignal(listing.id, status === 'going' ? 'pin' : 'save', listing.flyerMagnetURI);
    onReload?.();
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    deleteEventListing(listing.id, listing.pubkey);
    await publishEvent('delete_event_listing', { listingId: listing.id, pubkey: listing.pubkey });
    onBack();
    onReload?.();
  };

  const handleShare = () => {
    recordSignal(listing.id, 'share', listing.flyerMagnetURI);
    navigator.clipboard.writeText(`auth:event:${listing.id}`).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: SPACING.md, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: COLORS.highlight, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', padding: 0 }}
        >
          ← events
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg }}>
        <h1 style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '20px', fontWeight: 700, color: COLORS.text, margin: `0 0 ${SPACING.sm} 0` }}>
          {asText(listing.title)}
        </h1>

        <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', color: COLORS.highlight, marginBottom: SPACING.sm }}>
          {formatDateTime(listing.startTime)}
          {listing.endTime ? ` → ${formatDateTime(listing.endTime)}` : ''}
        </div>

        <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', color: COLORS.textMuted, marginBottom: SPACING.lg }}>
          {listing.venue ? `${asText(listing.venue)} · ` : ''}{asText(listing.city)}
        </div>

        <div style={{ display: 'flex', gap: SPACING.sm, marginBottom: SPACING.lg }}>
          <button
            onClick={() => handleRSVP('going')}
            style={{
              background: myRSVP === 'going' ? COLORS.accent : 'none',
              border: `2px solid ${myRSVP === 'going' ? COLORS.accent : COLORS.border}`,
              color: myRSVP === 'going' ? '#fff' : COLORS.text,
              padding: `${SPACING.sm} ${SPACING.lg}`,
              cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px',
            }}
          >
            ✓ going ({counts.going})
          </button>
          <button
            onClick={() => handleRSVP('interested')}
            style={{
              background: myRSVP === 'interested' ? COLORS.panel : 'none',
              border: `2px solid ${COLORS.border}`,
              color: COLORS.textMuted,
              padding: `${SPACING.sm} ${SPACING.lg}`,
              cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px',
            }}
          >
            ? interested ({counts.interested})
          </button>
          <button
            onClick={handleShare}
            style={{ background: 'none', border: 'none', color: shared ? COLORS.highlight : COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', padding: 0 }}
          >
            {shared ? '✓ copied' : '⇥ share'}
          </button>
          {listing.pubkey === identity.publicKey && (
            <button
              onClick={handleDeleteEvent}
              style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', padding: 0 }}
            >
              🗑 delete
            </button>
          )}
          {listing.pubkey !== identity.publicKey && (
            <ReportButton targetId={listing.id} targetType="event_listing" />
          )}
        </div>

        {listing.description && (
          <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '14px', lineHeight: '1.6', color: COLORS.text, whiteSpace: 'pre-wrap', marginBottom: SPACING.lg }}>
            {asText(listing.description)}
          </div>
        )}

        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: SPACING.xs, flexWrap: 'wrap', marginBottom: SPACING.lg }}>
            {tags.map(tag => (
              <span
                key={tag}
                style={{
                  fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px',
                  color: COLORS.textMuted, border: `1px solid ${COLORS.border}`,
                  padding: `${SPACING.xs} ${SPACING.sm}`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {listing.links && listing.links.length > 0 && (
          <div>
            <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px', color: COLORS.textMuted, marginBottom: SPACING.xs }}>
              links
            </div>
            {listing.links.map((link, i) => (
              <div key={i} style={{ marginBottom: SPACING.xs }}>
                <ExternalLink href={link.url}>{asText(link.label) || link.url}</ExternalLink>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
